import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Konfirmations-Dialog für Kontakt-Löschen mit Vorabprüfung der Verwendung.
 * Zeigt offene & historische Belege an. Erlaubt Force-Delete bei rein historischen
 * Verknüpfungen (Belege bleiben erhalten).
 */
export function ContactDeleteDialog({
  open,
  onOpenChange,
  companyId,
  contact, // { _id, name }
  kind = "Kontakt", // "Kunde" | "Lieferant"
  onDeleted,
}) {
  const [usage, setUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !contact?._id) return;
    let active = true;
    setLoadingUsage(true);
    setError("");
    setUsage(null);
    apiClient
      .get(`/companies/${companyId}/contacts/${contact._id}/usage`)
      .then((r) => active && setUsage(r.data))
      .catch((e) =>
        active &&
        setError(e.response?.data?.detail || "Verwendungs-Check fehlgeschlagen."),
      )
      .finally(() => active && setLoadingUsage(false));
    return () => {
      active = false;
    };
  }, [open, contact?._id, companyId]);

  const hasOpen = usage && (usage.open_invoices > 0 || usage.open_bills > 0);
  const hasPaid = usage && (usage.paid_invoices > 0 || usage.paid_bills > 0);

  const handleDelete = async (force) => {
    if (!contact?._id) return;
    setDeleting(true);
    setError("");
    try {
      await apiClient.delete(
        `/companies/${companyId}/contacts/${contact._id}`,
        { params: force ? { force: true } : {} },
      );
      toast.success(
        force
          ? `${kind} gelöscht (historische Belege bleiben erhalten)`
          : `${kind} "${contact.name}" gelöscht`,
      );
      onOpenChange(false);
      onDeleted?.(contact._id);
    } catch (e) {
      setError(e.response?.data?.detail || "Fehler beim Löschen.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="max-w-md"
        data-testid="contact-delete-dialog"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-rose-600" />
            {kind} löschen?
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <span className="block">
              Du löschst <strong>{contact?.name}</strong>. Diese Aktion kann nicht
              rückgängig gemacht werden.
            </span>

            {loadingUsage && (
              <span className="flex items-center gap-1.5 text-xs text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin" /> Prüfe Verwendung …
              </span>
            )}

            {usage && hasOpen && (
              <span className="block rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
                <strong className="font-semibold">Löschen blockiert:</strong>{" "}
                {usage.open_invoices > 0 && (
                  <span>
                    {usage.open_invoices} offene Rechnung(en)
                    {usage.open_bills > 0 ? ", " : ""}
                  </span>
                )}
                {usage.open_bills > 0 && (
                  <span>{usage.open_bills} offene Eingangsrechnung(en)</span>
                )}
                . Bitte zuerst abschliessen oder stornieren.
              </span>
            )}

            {usage && !hasOpen && hasPaid && (
              <span className="block rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <strong className="font-semibold">Hinweis:</strong>{" "}
                Es existieren historische Verknüpfungen
                {usage.paid_invoices > 0 && (
                  <> ({usage.paid_invoices} bezahlte Rechnung(en))</>
                )}
                {usage.paid_bills > 0 && (
                  <> ({usage.paid_bills} bezahlte Eingangsrechnung(en))</>
                )}
                . Beim Löschen bleiben diese Belege erhalten – nur der Kontakt
                wird entfernt.
              </span>
            )}

            {usage && !hasOpen && !hasPaid && (
              <span className="block text-xs text-slate-500">
                Keine offenen oder historischen Verknüpfungen gefunden.
              </span>
            )}

            {error && (
              <span className="block rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
                {error}
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="contact-delete-cancel" disabled={deleting}>
            Abbrechen
          </AlertDialogCancel>
          {!hasOpen && (
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-300"
              disabled={deleting || loadingUsage}
              onClick={(e) => {
                e.preventDefault();
                handleDelete(hasPaid);
              }}
              data-testid="contact-delete-confirm"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Lösche …
                </>
              ) : hasPaid ? (
                "Trotzdem löschen"
              ) : (
                "Endgültig löschen"
              )}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
